import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import {
  BlocksTemplateVariablesType,
  useBlocksConfig,
  useEditorContent,
  useEditorDispatch
} from "@pathwright/blocks-engine"
import {
  CautionTriangleIcon,
  CheckIcon,
  ChevronDownIcon,
  InfoCircleIcon
} from "@pathwright/pathicons"
// @ts-ignore
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import pluralize from "pluralize"
import { useEffect, useRef, useState } from "react"
import PathwrightChakraTooltip from "../ui/PathwrightChakraTooltip"
import ContentPublishDiff from "./ContentPublishDiff"

const Indicator = ({ needsPublish }: { needsPublish?: boolean }) => {
  return needsPublish ? (
    <Box
      borderRadius="50%"
      height="12px"
      width="12px"
      bg="var(--chakra-colors-red-500)"
      position="absolute"
      right="-2px"
      top="-2px"
    />
  ) : null
}

type ContentPublishActionProps = {
  needsPublish: boolean
}

const ContentPublishAction = ({ needsPublish }: ContentPublishActionProps) => {
  const { templateVariables } = useBlocksConfig()
  const { isOpen, onToggle, onClose } = useDisclosure()

  const [discardingDraft, setDiscardingDraft] = useState(false)

  const { resource, step, group } =
    templateVariables as BlocksTemplateVariablesType
  const { changes, syncing } = useEditorContent()
  const { handleDiscardDraftContent, handlePublishContent } =
    useEditorDispatch()
  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const { t, tc, tca, Trans } = useTranslate()
  const isMultiStepChange =
    step?.id !== step?.sync_source_id && (resource?.num_offerings || 0) > 1
  const cohortName = group?.name || "this Cohort"
  const resourceName = resource?.name || "this Path"
  const isSourcePath = group?.is_curriculum || false

  // For some reason, non-user blur events are being triggered,
  // so we need to add our own handler for toggling the popover.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node | null) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node | null)
      ) {
        onClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <HStack>
      <Popover isOpen={isOpen} closeOnBlur={false}>
        <PopoverTrigger>
          <Button
            ref={triggerRef}
            onClick={onToggle}
            variant="outline"
            colorScheme="gray"
            borderRadius="50px"
            borderColor="gray.400"
            bg="blackAlpha.50"
            _hover={{
              bg: "blackAlpha.100"
            }}
            px={4}
            size="large"
            fontWeight="600"
            leftIcon={
              needsPublish ? undefined : (
                <CheckIcon size="1em" color="var(--chakra-colors-gray-600)" />
              )
            }
            rightIcon={
              syncing ? (
                <Spinner size="sm" color="gray.600" />
              ) : (
                <ChevronDownIcon
                  size="1em"
                  color="var(--chakra-colors-gray-600)"
                />
              )
            }
            pos="relative"
            lineHeight="1"
            h={9}
            cursor="pointer"
            opacity={needsPublish ? 1 : 0.4}
            isDisabled={syncing}
          >
            {needsPublish ? tc("Save") : tc("Saved")}
            <Indicator needsPublish={needsPublish} />
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            ref={popoverRef}
            sx={{
              p: { marginTop: 0, marginBottom: 0 }
            }}
          >
            <PopoverArrow />
            <PopoverBody
              p={4}
              maxH={`calc(100vh - 220px)`}
              overflowY="auto"
              overflowX="hidden"
            >
              <VStack
                textAlign="center"
                minWidth="250px"
                width="full"
                gap={"16px"}
                sx={{
                  "p:last-of-type": {
                    paddingTop: 0
                  }
                }}
                borderRadius="10px"
              >
                <VStack justify="center" w="full" gap={1}>
                  <Pathicon
                    aria-label="block-icon"
                    icon={
                      needsPublish ? "block-notification-fill" : "block-fill"
                    }
                    display="flex"
                    alignItems="center"
                    height="27px"
                    width="27px"
                    p={0}
                    m="0 10px"
                    color="rgb(170, 170, 170)"
                    fontSize="1.2em"
                    cursor={"default"}
                    bg="transparent"
                    sx={{
                      svg: {
                        height: "27px",
                        width: "27px"
                      }
                    }}
                    _hover={undefined}
                    _active={{
                      bg: "transparent"
                    }}
                  />
                  <Heading
                    as="h3"
                    size="md"
                    textAlign="center"
                    fontWeight={700}
                    my={0}
                    pos="relative"
                  >
                    <Text as="span" fontWeight={700}>
                      {needsPublish ? (
                        <>
                          {changes?.blocks?.length || ""}{" "}
                          {tca(pluralize("Change", changes?.blocks?.length))}
                        </>
                      ) : (
                        tc("All changes saved")
                      )}
                    </Text>
                    <Box pos="absolute" right="-21px" top="-7px">
                      <PathwrightChakraTooltip
                        label={
                          <Text
                            fontSize="sm"
                            textAlign="left"
                            color="white"
                            lineHeight="1.4"
                          >
                            {tc(
                              `Blocks changes don’t require Publishing with the Path and are available to everyone as soon as you save them.`
                            )}{" "}
                            <Link
                              textDecoration="underline"
                              href="https://help.pathwright.com/en/articles/1756938-publish-button-for-blocks-content"
                            >
                              {tc("Learn more about saving Blocks")}
                            </Link>
                            .
                          </Text>
                        }
                      >
                        <IconButton
                          aria-label="info"
                          as={Link}
                          target="_blank"
                          href="https://help.pathwright.com/en/articles/1756938-publish-button-for-blocks-content"
                          icon={
                            <InfoCircleIcon
                              size="1em"
                              color="var(--chakra-colors-PRIMARY_GRAY)"
                            />
                          }
                          minW="1.1em"
                          variant="link"
                          py={0}
                          px={1}
                          bg={"transparent"}
                        />
                      </PathwrightChakraTooltip>
                    </Box>
                  </Heading>
                </VStack>

                {needsPublish ? (
                  <Text textAlign="center">
                    {tc("Save changes to share them with everyone")}
                    <Box as="span">
                      {" "}
                      {t("in")}{" "}
                      <strong>
                        {isMultiStepChange || isSourcePath
                          ? resourceName
                          : cohortName}
                      </strong>
                    </Box>
                    .
                  </Text>
                ) : (
                  <Text textAlign="center">
                    {tc(
                      "All Blocks in this Step are shared with with everyone"
                    )}
                    {resource?.name ? (
                      <Box as="span">
                        {" "}
                        {t("in")} <strong>{resource.name}</strong>
                      </Box>
                    ) : (
                      ""
                    )}
                    .
                  </Text>
                )}
                <ContentPublishDiff
                  onClickBlockChange={(blockId: string) => {
                    // smoothly navigate to #<blockId>
                    const block = document.getElementById(blockId)
                    if (block) {
                      block.scrollIntoView({
                        behavior: "smooth",
                        block: "center"
                      })
                    }
                    onClose()
                  }}
                  changes={changes}
                />
                {needsPublish ? (
                  <>
                    {isMultiStepChange ? (
                      <HStack
                        maxW="250px"
                        margin="20px auto"
                        align="center"
                        gap={3}
                      >
                        <CautionTriangleIcon
                          color="var(--chakra-colors-gray-700)"
                          size="1.4em"
                        />
                        <Text>
                          <Trans i18nKey="blocks_are_shared">
                            Blocks are{" "}
                            <Link
                              style={{ textDecoration: "underline" }}
                              href="https://help.pathwright.com/en/articles/1756938-publish-button-for-blocks-content#what-publishing-means"
                            >
                              shared across all Cohort Paths
                            </Link>
                          </Trans>
                        </Text>
                      </HStack>
                    ) : null}
                    <VStack my={2} gap={0} maxW="100%">
                      {discardingDraft ? (
                        <>
                          <Text textAlign="center">
                            {tc(
                              "This will remove all unsaved changes. Are you sure?"
                            )}
                          </Text>
                          <Button
                            my={2}
                            colorScheme="red"
                            onClick={() => {
                              setDiscardingDraft(false)
                              onClose()
                              handleDiscardDraftContent!()
                            }}
                            cursor="pointer"
                          >
                            {tc("Yes, discard changes")}
                          </Button>
                          <Button
                            mb={2}
                            colorScheme="gray"
                            onClick={() => setDiscardingDraft(false)}
                            cursor="pointer"
                          >
                            {tc("No, cancel")}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            mb={2}
                            isDisabled={!needsPublish}
                            colorScheme="brand"
                            onClick={() => {
                              onClose()
                              handlePublishContent!()
                            }}
                            cursor="pointer"
                            display="block"
                            textOverflow="ellipsis"
                            overflow="hidden"
                            whiteSpace="nowrap"
                          >
                            {isMultiStepChange
                              ? tc("Save Blocks to All Cohort Paths")
                              : tc("Save Blocks")}
                          </Button>
                          <Button
                            variant="link"
                            textDecoration="underline"
                            colorScheme="gray"
                            color="PRIMARY_GRAY"
                            bg="transparent"
                            _hover={{
                              bg: "transparent"
                            }}
                            onClick={() => setDiscardingDraft(true)}
                            cursor="pointer"
                          >
                            {tc("Or revert changes")}
                          </Button>
                        </>
                      )}
                    </VStack>
                  </>
                ) : null}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </HStack>
  )
}

export default ContentPublishAction
